import type { MaybeRef } from 'vue'
import type {
  ID,
  Maybe,
  ResponseErrors,
  Union,
  CurrentFile,
  FileModelType,
  SimpleDate,
  Discount,
  PostListItem,
  PostElement,
} from '@/types'
import { PostElementType } from '@/types'

export * from './lists'
export * from './banners'
export * from './products'
export * from './validation'
export * from './productFilters'
export * from './auth'
export * from './menu'
export * from './filters'

import { isProxy, isRef, toRaw, unref } from 'vue'

import { findByPropValue } from '@/helpers/lists'

import FileModel from '@/models/FileModel'

import {
  CUSTOMER_BLOG_PATH,
  CUSTOMER_CATALOG_PATH,
  DEFAULT_IMAGE_SRC,
  SORTABLE_IMAGE_FALSE_ID,
} from '@/constants'

let env = import.meta.env.DEV ? 'development' : 'production'
if (import.meta.env.MODE) env = import.meta.env.MODE

export function getFileUrl(
  path?: string,
  type: 'image' | 'video' = 'image',
  defaultValue = ''
) {
  if (!path) return defaultValue

  const list: Record<string, string> = {
    image: env === 'development' ? '/i' : import.meta.env.VITE_IMAGES,
    video: env === 'development' ? '/storage' : import.meta.env.VITE_FILES,
  }

  return `${list[type]}/${path}`
}

export function handleErrors(errors?: ResponseErrors | string[]) {
  if (!errors) return ''

  if (Array.isArray(errors)) return errors?.join(', ') ?? ''

  let errorList = ''

  if (Object.hasOwn(errors, 'other'))
    errorList = `${errorList} ${errors.other}\n`

  if (Object.hasOwn(errors, 'model'))
    errorList = `${errorList} ${errors.model}\n`

  if (Object.hasOwn(errors, 'validation')) {
    Object.entries(errors.validation ?? {}).forEach(
      ([key, list]: [key: string, list: string[]]) => {
        errorList = `${errorList} ${key}: ${list?.join(', ')}\n`
      }
    )
  }

  return errorList
}

export function get(
  object: { [key: string]: any } | null,
  key: string,
  defaultValue: Maybe<string> = '',
  separator = '.'
): any {
  const arrayKeys = key.split(separator)
  let currentLevel = object

  arrayKeys.forEach((currentKey) => {
    if (currentLevel !== null && Object.hasOwn(currentLevel, currentKey))
      currentLevel = currentLevel[currentKey]
    else currentLevel = null
  })

  return currentLevel ?? defaultValue
}

/**
 * Deep merge two objects / arrays.
 * @param target
 * @param ...sources
 */
export function mergeDeep(
  target: Record<any, any>,
  ...sources: any[]
): Record<any, any> {
  if (!sources.length) return target

  const source = sources.shift()

  if (isRecord(target) && isRecord(source)) {
    for (const key in source) {
      const s = source[key as keyof typeof source]
      const sIsArray = Array.isArray(s)

      if (isRecord(s)) {
        if (!target[key as keyof typeof target]) {
          Object.assign(target, { [key]: sIsArray ? [] : {} })
        }

        mergeDeep(target[key as keyof typeof target], s)
      } else {
        Object.assign(target, { [key]: s })
      }
    }
  }

  return mergeDeep(target, ...sources)
}

export function deleteDeep(
  target: Record<any, any>,
  model: Record<any, any>,
  deep?: boolean
) {
  Object.keys(target).forEach((key) => {
    if (!Object.hasOwn(model, key)) {
      delete target[key]
    } else if (deep && isRecord(model[key])) {
      deleteDeep(target[key], model[key])
    }
  })
}

export function clone(value?: Record<any, any>) {
  if (!value || !isRecord(value)) return value

  return JSON.parse(JSON.stringify(value))
}

export function unProxy<T = any>(value: MaybeRef<T>): T {
  if (isRef(value)) return unProxy(unref(value))

  if (isProxy(value)) return unProxy(toRaw(value))

  return value
}

export function isRecord(value?: any) {
  return !!value && typeof value === 'object'
}
/**
 * For objects
 * */
export function isEmpty(value: Record<any, any>) {
  return isRecord(value) && Object.keys(value).length === 0
}
/**
 * For primitive values
 * */
export function isNotEmpty(value?: Maybe<any>) {
  return value !== null && value !== undefined && value !== ''
}

/**
 * Merge with first founded empty item
 * */
export function mergeWithEmpty(items: any[], item: any) {
  const index = items.findIndex((thing) => !Number.isInteger(thing.id))
  mergeDeep(items[index], clone(unProxy(item)))
}

export function isDiff(items: any[], otherItems: any[]) {
  const keysToCheck = ['id', 'active']

  const isDiffLength = items.length !== otherItems.length
  if (isDiffLength) return true

  return otherItems.some((item: any, index: number) => {
    let isDiff = false

    keysToCheck.forEach(
      (key) => (isDiff = isDiff || item[key] !== items[index][key])
    )

    return isDiff
  })
}

export function prepareDate(
  date?: Maybe<Date | SimpleDate | number>,
  dateStyle: 'short' | 'full' | 'long' | 'medium' = 'short',
  timeStyle?: 'short' | 'full' | 'long' | 'medium',
  options?: Intl.DateTimeFormatOptions
): Date | string {
  if (!date) return ''

  const opts = options ?? {
    dateStyle,
    timeStyle,
  }

  return (date instanceof Date ? date : new Date(date)).toLocaleString(
    'ru',
    opts
  )
}

export function prepareTime(
  date?: Maybe<Date | string | number>,
  timeStyle?: 'short' | 'full' | 'long' | 'medium'
): Date | string {
  if (!date) return ''

  return (date instanceof Date ? date : new Date(date)).toLocaleTimeString(
    'ru',
    timeStyle
      ? {
          timeStyle,
        }
      : { hour: '2-digit', minute: '2-digit' }
  )
}

export function prepareIdParam(id: MaybeRef<Maybe<ID | string>>) {
  const param = unref(id)
  const isString = typeof unref(id) === 'string'

  if (isString) {
    return isNotEmpty(param) ? +(param as string) : null
  } else return (param ?? null) as Maybe<ID>
}
/**
 * Catalog: union
 * */
export function getUnionFromForm(
  form: { unions?: Union[] },
  model: string | undefined,
  category: string | undefined
): [Maybe<Union>, number] {
  const unions = form.unions ?? []
  const index = unions.findIndex(
    (union) => union.model === model && union.category === category
  )

  return [unions[index] ?? null, index]
}

export function prepareCatalogUrl(formUrl?: Maybe<string>, withHost = true) {
  return prepareSomeUrl(CUSTOMER_CATALOG_PATH, formUrl, withHost)
}

export function prepareBlogUrl(formUrl?: Maybe<string>, withHost = true) {
  return prepareSomeUrl(CUSTOMER_BLOG_PATH, formUrl, withHost)
}

export function prepareSomeUrl(
  somePath: string,
  formUrl?: Maybe<string>,
  withHost = true
) {
  const host = import.meta.env.VITE_HOST ?? ''
  const path = withHost ? `${host}${somePath}` : `${somePath}`

  if (!formUrl) return `${path}`

  return `${path}${formUrl}`
}

export function prepareFormUrl(
  path: string,
  host: string,
  preparedHost: string,
  fieldUrl?: Maybe<string>
) {
  if (!fieldUrl) return ''

  if (fieldUrl.startsWith(path)) {
    return cutExcess(fieldUrl.split(path)[1], preparedHost)
  }
  if (fieldUrl.startsWith(host)) {
    return cutExcess(fieldUrl.split(host)[1], preparedHost)
  }

  return cutExcess(fieldUrl, preparedHost)
}

export function cutExcess(s: string, preparedHost?: string) {
  const strings = ['https://www.', 'https://', 'www.', '/']
  let tail = s

  if (preparedHost) strings.unshift(preparedHost)

  strings.forEach((item) => {
    if (tail.startsWith(item)) tail = tail.substring(item.length)
  })

  if (tail.endsWith('/')) tail = tail.slice(0, -1)

  return tail
}

export function prepareFixedMaskChars(path?: string) {
  if (!path) return ''

  const chars = ['0', 'a']

  chars.forEach((char: string) => {
    path = path?.replace(new RegExp(char, 'g'), `\\${char}`)
  })

  return `${path}`
}

export function compareFiles(
  newValue: Maybe<CurrentFile | FileModelType> | undefined,
  oldValue: FileModelType | undefined
) {
  return !isEmpty(
    FileModel.onlyModelKeysDiff(
      FileModel.mergeTogether(oldValue ?? {}),
      newValue ?? {}
    )
  )
}

export function hasNewFile(arr: Maybe<CurrentFile>[]) {
  return arr.some(
    (image) =>
      image &&
      ((image.id === null && image.file) ||
        (image.id ?? 0) >= SORTABLE_IMAGE_FALSE_ID)
  )
}

export function preparePhoneNumber(
  number?: Maybe<string>,
  plus = true,
  defaultEmpty = ''
) {
  if (!number) return defaultEmpty

  const startsWith = plus ? '+7' : '8'

  let phone = number.replace(/[^0-9]/g, '')

  if ((phone.startsWith('7') && plus) || phone.startsWith('8')) {
    phone = phone.substring(1)
  }

  return phone.replace(
    /(\d{3})(\d{3})(\d{2})(\d{2})/g,
    `${startsWith} ($1) $2-$3-$4`
  )
}

export function downloadURI(uri?: string) {
  if (!uri) return

  const link = document.createElement('a')
  link.href = uri

  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

export function checkSettingValues(current: any, item: any) {
  const keys = Object.keys(current.value)

  return keys.every(
    (key) =>
      current.value[key as keyof typeof current.value] === item?.value[key]
  )
}

export function getPersonalDiscount(discounts: Discount[]): Maybe<Discount> {
  return findByPropValue(discounts, 'personal', 'type')
}

export function formatDate(date: Date) {
  const offset = date.getTimezoneOffset()

  date = new Date(date.getTime() - offset * 60 * 1000)

  return date.toISOString().split('T')[0]
}

export function truncate(text?: string, maxLength = 40) {
  if (!text) return ''

  return text.length > maxLength ? text.slice(0, maxLength - 1) + '…' : text
}

export function getFilePath(
  path?: Maybe<string>,
  type: 'image' | 'video' = 'image'
) {
  if (!path) return DEFAULT_IMAGE_SRC

  const folder = type === 'image' ? 'm_upload/' : ''

  const url = getFileUrl(`${folder}${path}`, type)
  if (!url) return DEFAULT_IMAGE_SRC

  return url
}

export function getPostImage(post: PostListItem) {
  const visiblePostings = post.postings.filter((posting) => posting.active)
  const element: PostElement = findByPropValue(
    visiblePostings,
    PostElementType.PhotoWide,
    'type'
  )

  return getFilePath(element?.files?.[0]?.path)
}

export function prepareQueryParams(params: Record<string, any>) {
  const getPairs = (obj: Record<string, any>, keys: string[] = []) =>
    Object.entries(obj).reduce((pairs: any[], [key, value]) => {
      if (typeof value === 'object')
        pairs.push(...getPairs(value, [...keys, key]))
      else pairs.push([[...keys, key], value])
      return pairs
    }, [])

  return getPairs(params)
    .map(
      ([[key0, ...keysRest], value]) =>
        `${key0}${keysRest.map((a: any) => `[${a}]`).join('')}=${value}`
    )
    .join('&')
}
